import {
  createAsyncThunk,
  createListenerMiddleware,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { RootState } from ".";
import { request } from "../services/cpln";
import { fetchGvcs, fetchHasWorkload } from "./userDataSlice";
import { fetchCustomLogo } from "./restSlice";
import { STORAGE_KEY_ORG_TIMEOUT_SECONDS } from "../envVariables";

interface ContextState {
  timeoutSeconds: number;
  org: string;
  gvc: string;
  linkedAccountId: string;
  isChangingContext: boolean;
  redirect: string | null;
}

const initialState: ContextState = {
  timeoutSeconds: 15 * 60,
  org: "",
  gvc: "",
  linkedAccountId: "",
  isChangingContext: false,
  redirect: null,
};

export const fetchOrgTimeoutSeconds = createAsyncThunk("context/fetchOrgTimeoutSeconds", async (org: string) => {
  const { data: orgItem } = await request({ url: `/org/${org}` });
  return orgItem.spec?.sessionTimeoutSeconds || 15 * 60;
});

export const initializeContext = createAsyncThunk(
  "context/initializeContext",
  async (orgNames: string[], { dispatch }) => {
    const url = window.location.pathname;

    let gvcFromUrl = "";
    let orgFromUrl = "";
    try {
      orgFromUrl = url.split("/console/org/")[1].split("/")[0];
    } catch (e) {}
    try {
      if (orgFromUrl) {
        gvcFromUrl = url.split(`/console/org/${orgFromUrl}/gvc/`)[1].split("/")[0];
      }
    } catch (e) {}

    let contextOrg = "";
    if (orgFromUrl) {
      if (orgNames.includes(orgFromUrl)) {
        contextOrg = orgFromUrl;
      } else {
        dispatch(setOrgAndGvc({ org: "", gvc: "" }));
        return;
      }
    }

    if (!contextOrg) {
      const localTabOrg = "";
      const localOrg = window.localStorage.getItem(`org`);
      let orgToSet = localTabOrg || localOrg;
      if (orgToSet && orgNames.includes(orgToSet)) {
        contextOrg = orgToSet;
      } else {
        if (orgNames.length === 1) {
          contextOrg = orgNames[0];
        }
      }
    }

    let contextGvc = "";
    if (contextOrg) {
      const localGvc = window.localStorage.getItem(`${contextOrg}`);
      const gvcsToCheck = [gvcFromUrl, localGvc].filter(Boolean) as string[];

      for (const gvcToCheck of gvcsToCheck) {
        try {
          await request({ url: `/org/${contextOrg}/gvc/${gvcToCheck}` });
          contextGvc = gvcToCheck;
          break;
        } catch (e) {
          // GVC doesn't exist, try next
        }
      }
    }

    dispatch(setOrgAndGvc({ org: contextOrg, gvc: contextGvc }));
  },
);

export const contextSlice = createSlice({
  name: "context",
  initialState,
  reducers: {
    setIsChangingContext: (state, action: PayloadAction<boolean>) => {
      state.isChangingContext = action.payload;
    },
    setRedirect: (state, action: PayloadAction<string | null>) => {
      state.redirect = action.payload;
    },
    setOrgAndGvc(state, action: PayloadAction<{ org: string; gvc: string }>) {
      state.org = action.payload.org;
      state.gvc = action.payload.gvc;
    },
    setOrg(state, action: PayloadAction<string>) {
      state.org = action.payload;
      state.gvc = "";
    },
    setGvc(state, action: PayloadAction<string>) {
      state.gvc = action.payload;
    },
    setLinkedAccountId(state, action: PayloadAction<string>) {
      state.linkedAccountId = action.payload;
    },
    setTimeoutSeconds(state, action: PayloadAction<number>) {
      state.timeoutSeconds = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetch account summaries states
      .addCase(fetchOrgTimeoutSeconds.fulfilled, (state, action) => {
        state.timeoutSeconds = action.payload;
      })
      .addCase(fetchOrgTimeoutSeconds.rejected, (state) => {
        state.timeoutSeconds = 15 * 60;
      });
  },
});

export const {
  setIsChangingContext,
  setRedirect,
  setOrgAndGvc,
  setOrg,
  setGvc,
  setLinkedAccountId,
  setTimeoutSeconds,
} = contextSlice.actions;

export const contextReducer = contextSlice.reducer;

export const selectOrg = (state: RootState) => state.context.org;
export const selectGvc = (state: RootState) => state.context.gvc;

export const selectContext = createSelector([selectOrg, selectGvc], (org, gvc) => {
  return { org, gvc };
});

export const contextListenerMiddleware = createListenerMiddleware();

contextListenerMiddleware.startListening({
  actionCreator: setOrgAndGvc,
  effect: async (action, listenerApi) => {
    listenerApi.dispatch(fetchCustomLogo());
    listenerApi.dispatch(fetchOrgTimeoutSeconds(action.payload.org));

    const state = listenerApi.getState() as RootState;
    window.localStorage.setItem(`org`, action.payload.org);
    window.localStorage.setItem(`${state.uiData.tabId}-org`, action.payload.org);
    window.localStorage.setItem(action.payload.org, action.payload.gvc);
    window.localStorage.setItem(`${state.uiData.tabId}-${action.payload.org}`, action.payload.gvc);
  },
});

contextListenerMiddleware.startListening({
  actionCreator: setOrg,
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState() as RootState;
    window.localStorage.setItem(`org`, action.payload);
    window.localStorage.setItem(`${state.uiData.tabId}-org`, action.payload);

    listenerApi.dispatch(fetchCustomLogo());
    listenerApi.dispatch(fetchOrgTimeoutSeconds(action.payload));

    const gvcs = await listenerApi.dispatch(fetchGvcs()).unwrap();
    const gvcNames = gvcs.map((gvc) => gvc.name);
    const localGvc = window.localStorage.getItem(`${action.payload}`) || "";
    const localTabGvc = window.localStorage.getItem(`${state.uiData.tabId}-${action.payload}`) || "";
    const gvcsToSet = [localGvc, localTabGvc];
    for (const gvcToSet of gvcsToSet) {
      if (!!gvcToSet && gvcNames.includes(gvcToSet)) {
        listenerApi.dispatch(setGvc(gvcToSet));
        break;
      }
    }
  },
});

contextListenerMiddleware.startListening({
  actionCreator: setGvc,
  effect: async (action, listenerApi) => {
    listenerApi.dispatch(fetchHasWorkload());

    const state = listenerApi.getState() as RootState;
    window.localStorage.setItem(state.context.org, action.payload);
    window.localStorage.setItem(`${state.uiData.tabId}-${state.context.org}`, action.payload);
  },
});

contextListenerMiddleware.startListening({
  actionCreator: fetchOrgTimeoutSeconds.fulfilled,
  effect: async (action, listenerApi) => {
    localStorage.setItem(STORAGE_KEY_ORG_TIMEOUT_SECONDS, String(action.payload));
  },
});

contextListenerMiddleware.startListening({
  actionCreator: fetchOrgTimeoutSeconds.rejected,
  effect: async (action, listenerApi) => {
    localStorage.removeItem(STORAGE_KEY_ORG_TIMEOUT_SECONDS);
  },
});
